import axios, { AxiosError, type AxiosInstance } from "axios";
import { indexFromName } from "../drive-naming";
import type { ScriptMetadataPayload } from "../script";
import {
  ConnectivityException,
  StoreAuthException,
  StoreConflictException,
  StoreUnreachableException,
} from "./errors";

export interface RemoteFile {
  id: string;
  size: number;
  sha256: string | null;
}

export interface DeviceHeartbeat {
  deviceId: string;
  scannerName: string;
  waiting: number;
  blocked: number;
  needsRetake: number;
  maxAttempts: number;
  lastFailReason: string | null;
  pauseReason: string | null;
  /** RFC3339, or null when nothing is waiting. */
  oldestWaitingAt: string | null;
}

export interface DeviceHeartbeatReply {
  flushRequestedAt: string | null;
}

export interface ReviewStateRow {
  id: string;
  status: "pending" | "flagged" | "approved";
  flagMessage: string | null;
  flagAckedAt: string | null;
  flagReply: string | null;
}

export class NyntaxHttpStore {
  private client: AxiosInstance;

  constructor(base: string, token: string) {
    this.client = axios.create({
      baseURL: base,
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  async ensureScript(scriptId: string, scannerName: string, metadata: ScriptMetadataPayload): Promise<string> {
    return this.guard(async () => {
      await this.client.post("/api/scripts", { id: scriptId, scannerName, ...metadata });
      return scriptId;
    });
  }

  /** No-op locally if the script hasn't reached the server yet — the next
   * ensureScript call carries the current metadata anyway. */
  async patchScriptMetadata(scriptId: string, metadata: ScriptMetadataPayload): Promise<void> {
    return this.guard(async () => {
      await this.client.patch(`/api/scripts/${scriptId}`, metadata);
    });
  }

  async findPage(scriptId: string, name: string): Promise<RemoteFile | null> {
    return this.guard(async () => {
      const index = indexFromName(name);
      try {
        const res = await this.client.get(`/api/scripts/${scriptId}/pages/${index}`);
        return { id: res.data.id, size: res.data.byteSize, sha256: res.data.sha256 ?? null };
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 404) return null;
        throw e;
      }
    });
  }

  /** `replace: true` tells the server this is a retake: overwrite the image
   * already stored under this page index instead of answering 409
   * `remote-mismatch`. The page keeps its id, indices, and reviewer
   * annotations server-side. */
  async uploadPage(
    scriptId: string,
    name: string,
    blob: Blob,
    sha256: string,
    options: { replace?: boolean } = {},
  ): Promise<RemoteFile> {
    return this.guard(async () => {
      const index = indexFromName(name);
      const res = await this.client.put(`/api/scripts/${scriptId}/pages/${index}`, blob, {
        headers: {
          "Content-Type": "image/jpeg",
          "X-Checksum-Sha256": sha256,
          ...(options.replace ? { "X-Replace-Page": "1" } : {}),
        },
      });
      return { id: res.data.id, size: res.data.byteSize, sha256: res.data.sha256 ?? null };
    });
  }

  async reviewState(ids: string[]): Promise<ReviewStateRow[]> {
    return this.guard(async () => {
      const res = await this.client.post("/api/scripts/review-state", { ids });
      return res.data;
    });
  }

  /** Reports this device's queue and picks up any flush the desk asked
   * for. One call does both on purpose: the device that most needs to be
   * told "push everything now" is the one whose uploads are failing, and
   * this endpoint is small enough to succeed where a page upload doesn't. */
  async heartbeat(report: DeviceHeartbeat): Promise<DeviceHeartbeatReply> {
    return this.guard(async () => {
      const res = await this.client.post("/api/devices/heartbeat", report);
      return { flushRequestedAt: res.data.flushRequestedAt ?? null };
    });
  }

  async ackFlag(scriptId: string, reply?: string | null): Promise<void> {
    return this.guard(async () => {
      const body = reply != null && reply !== "" ? { reply } : {};
      await this.client.post(`/api/scripts/${scriptId}/flag-ack`, body);
    });
  }

  private async guard<T>(action: () => Promise<T>): Promise<T> {
    try {
      return await action();
    } catch (e) {
      if (
        e instanceof StoreAuthException ||
        e instanceof StoreUnreachableException ||
        e instanceof StoreConflictException
      ) {
        throw e;
      }
      if (e instanceof AxiosError) {
        const status = e.response?.status;
        if (status === 401 || status === 403) throw new StoreAuthException();
        if (status === 409) throw new StoreConflictException();
        if (status === 503 || status === 507) throw new StoreUnreachableException();
        if (!e.response) throw new ConnectivityException();
        if (status && status >= 400) throw new Error(`status ${status}`);
      }
      throw e;
    }
  }
}
